import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../../components/ui/select';
import { Plus, X, Upload, FileSpreadsheet, Loader2 } from 'lucide-react';
import { Division26ListPage } from './shared/Division26ListPage';
import { Division26CreateModal } from './shared/Division26CreateModal';
import { Division26ReviewModal } from './shared/Division26ReviewModal';
import {
  deviceListConfig, deviceFormConfig, deviceReviewConfig,
  DEVICE_CATEGORIES, DEVICE_TYPE_TO_CATEGORY, ALL_CATEGORIES,
} from './deviceConfig';

export const DeviceSection = ({
  projectId, csiItem, canEditCSIItems, canManageProject,
  onItemCount,
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [reviewingItem, setReviewingItem] = useState(null);

  // Filters
  const [categoryFilter, setCategoryFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [mfrFilter, setMfrFilter] = useState('');

  // Import Excel
  const [importing, setImporting] = useState(false);
  const importRef = useRef(null);

  const csiItemId = csiItem?.id;

  useEffect(() => {
    if (onItemCount) onItemCount(items.length);
  }, [items.length, onItemCount]);

  const fetchItems = useCallback(async () => {
    if (!projectId) return;
    try {
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.get(`${API}${deviceListConfig.apiBase}/${projectId}${params}`);
      setItems(res.data);
    } catch {
      toast.error('Failed to load devices');
    } finally {
      setLoading(false);
    }
  }, [projectId, csiItemId]);

  useEffect(() => { fetchItems(); }, [fetchItems]);

  // Derive unique filter values
  const uniqueMfrs = useMemo(() => [...new Set(items.map(i => i.manufacturer).filter(Boolean))].sort(), [items]);
  const uniqueTypes = useMemo(() => [...new Set(items.map(i => i.deviceType).filter(Boolean))].sort(), [items]);
  const uniqueCategories = useMemo(() => [...new Set(items.map(i => i.category).filter(Boolean))].sort(), [items]);

  // Filter
  const filteredItems = useMemo(() => {
    let result = items;
    if (categoryFilter) result = result.filter(i => i.category === categoryFilter);
    if (typeFilter) result = result.filter(i => i.deviceType === typeFilter);
    if (mfrFilter) result = result.filter(i => i.manufacturer === mfrFilter);
    return result;
  }, [items, categoryFilter, typeFilter, mfrFilter]);

  const hasFilters = categoryFilter || typeFilter || mfrFilter;
  const clearFilters = () => { setCategoryFilter(''); setTypeFilter(''); setMfrFilter(''); };

  // Single open selectbox behavior
  const [openDropdown, setOpenDropdown] = useState(null);
  const makeDropdownProps = (key) => ({
    open: openDropdown === key,
    onOpenChange: (open) => setOpenDropdown(prev => open ? key : (prev === key ? null : prev)),
    modal: false,
  });

  // ── Import / Sample Excel ──
  const handleSampleDownload = async () => {
    try {
      const res = await axios.get(`${API}${deviceListConfig.apiBase}/${projectId}/sample-excel`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a'); a.href = url; a.download = 'device_sample.xlsx'; a.click();
      window.URL.revokeObjectURL(url);
    } catch { toast.error('Failed to download sample'); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData(); fd.append('file', file);
      const params = csiItemId ? `?csi_item_id=${csiItemId}` : '';
      const res = await axios.post(`${API}${deviceListConfig.apiBase}/${projectId}/import-excel${params}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const { imported, skipped, errors } = res.data;
      if (imported > 0 && skipped === 0) {
        toast.success(`${imported} device(s) imported successfully`);
      } else if (imported > 0 && skipped > 0) {
        toast.warning(`${imported} imported, ${skipped} skipped${errors?.length ? `: ${errors[0]}` : ''}`);
      } else if (imported === 0) {
        toast.error(`No devices imported. ${skipped} row(s) skipped${errors?.length ? `. ${errors[0]}` : ''}`);
      }
      fetchItems();
    } catch (err) { toast.error(err.response?.data?.detail || 'Import failed'); }
    finally { setImporting(false); if (importRef.current) importRef.current.value = ''; }
  };

  // ── CRUD ──
  const handleSave = async (formData, existingItem) => {
    const payload = { ...formData, csiItemId };
    // Parse numeric fields
    if (payload.voltage !== '' && payload.voltage != null) payload.voltage = parseFloat(payload.voltage);
    else payload.voltage = null;
    if (payload.wattage !== '' && payload.wattage != null) payload.wattage = parseFloat(payload.wattage);
    else payload.wattage = null;

    if (existingItem) {
      await axios.put(`${API}${deviceListConfig.apiBase}/${projectId}/${existingItem.id}`, payload);
      toast.success('Device updated');
    } else {
      await axios.post(`${API}${deviceListConfig.apiBase}/${projectId}`, payload);
      toast.success('Device created');
    }
    fetchItems();
  };

  const handleDelete = async (id) => {
    await axios.delete(`${API}${deviceListConfig.apiBase}/${projectId}/${id}`);
    setItems(prev => prev.filter(e => e.id !== id));
  };

  const handleBulkDelete = async (ids) => {
    const res = await axios.post(`${API}${deviceListConfig.apiBase}/${projectId}/bulk-delete`, { ids });
    setItems(prev => prev.filter(e => !ids.includes(e.id)));
    return res.data.deleted || 0;
  };

  const handleEditClick = (item) => {
    setEditingItem({ ...item });
    setShowModal(true);
  };

  const handleReviewClick = (item) => {
    // Add derived display fields for review
    setReviewingItem({
      ...item,
      voltageDisplay: item.voltage != null ? `${item.voltage} V` : '-',
    });
  };

  const handleModalClose = (open) => {
    if (!open) {
      setShowModal(false);
      setEditingItem(null);
    }
  };

  // ── Filter Bar ──
  const filterBar = (
    <>
      <Select value={categoryFilter} onValueChange={(val) => setCategoryFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('category')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="device-filter-category">
          <SelectValue placeholder="All Categories" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Categories</SelectItem>
          {uniqueCategories.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={typeFilter} onValueChange={(val) => setTypeFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('type')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="device-filter-type">
          <SelectValue placeholder="All Device Types" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Device Types</SelectItem>
          {uniqueTypes.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
        </SelectContent>
      </Select>

      <Select value={mfrFilter} onValueChange={(val) => setMfrFilter(val === '__all__' ? '' : val)} {...makeDropdownProps('manufacturer')}>
        <SelectTrigger className="w-40 h-8 text-xs" data-testid="device-filter-manufacturer">
          <SelectValue placeholder="All Manufacturers" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="__all__">All Manufacturers</SelectItem>
          {uniqueMfrs.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}
        </SelectContent>
      </Select>

      {hasFilters && (
        <Button variant="ghost" size="sm" className="h-8 text-xs text-slate-500" onClick={clearFilters} data-testid="device-clear-filters-btn">
          <X className="h-3.5 w-3.5 mr-1" />Clear
        </Button>
      )}
    </>
  );

  return (
    <>
      <Division26ListPage
        config={deviceListConfig}
        items={items}
        loading={loading}
        projectId={projectId}
        csiItem={csiItem}
        canEditCSIItems={canEditCSIItems}
        canManageProject={canManageProject}
        onCreateClick={() => { setEditingItem(null); setShowModal(true); }}
        onEditClick={handleEditClick}
        onReviewClick={handleReviewClick}
        onDelete={handleDelete}
        onBulkDelete={handleBulkDelete}
        filterBar={filterBar}
        filteredItems={filteredItems}
        showShare={false}
        extraToolbarButtons={
          <>
            <Button variant="outline" size="sm" onClick={handleSampleDownload} data-testid="elec-device-sample-btn">
              <FileSpreadsheet className="h-4 w-4 mr-1.5" />Sample Excel
            </Button>
            <Button variant="outline" size="sm" onClick={() => importRef.current?.click()} disabled={importing} data-testid="elec-device-import-btn">
              {importing ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}Import Excel
            </Button>
            <input ref={importRef} type="file" accept=".xlsx,.xls" className="hidden" onChange={handleImport} />
          </>
        }
      />

      <Division26CreateModal
        open={showModal}
        onOpenChange={handleModalClose}
        config={deviceFormConfig}
        editItem={editingItem}
        onSave={handleSave}
      />

      <Division26ReviewModal
        open={!!reviewingItem}
        onOpenChange={(open) => { if (!open) setReviewingItem(null); }}
        config={deviceReviewConfig}
        item={reviewingItem}
      />
    </>
  );
};
